import React, { Dispatch, SetStateAction, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Product } from '../../models/product.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Settings } from '../../models/settings.model';
import { ProductDA } from '../../services/admin/product.service';
import { legendInvalid, legendIconInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { isValidObjectAttributes } from '../../scripts/compare.object.script';
import { suffixLegend } from '../../scripts/name.legend.script';

import AppProductVariantForm from '../../interfaces/main/inventory/product/component/ProductVariantForm';
import AppLoading from '../Loading';
import AppModalSettingCreateUnit from './ModalSettingsCreateUnit';

export interface AppModalProductVariantAddProps {
  companyForUser: Company | null,
  settingsForCompany: Settings | null,
  product?: Product | null,
  variantDefault: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  variant: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  variants: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]},
  setVariant: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>,
  setVariants: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]}>>,
  loadSettingsForCompany: Function
};

const AppModalProductVariantAdd: React.FunctionComponent<AppModalProductVariantAddProps> = ({companyForUser, settingsForCompany, product, variantDefault, variant, variants, setVariant, setVariants, loadSettingsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')

  const resolveUnit = (unitId: string) => {
    const mu = settingsForCompany?.measure_units?.find((m: any) => m.id === unitId)
    return mu ? { id: mu.id, name: mu.name, type: mu.type ?? 'product', symbol: mu.symbol ?? '' } : unitId
  }

  const mapApiVariantToState = (apiVariant: any) => {
    return {
      id: apiVariant.id ?? apiVariant._id ?? '',
      description_short: apiVariant.description_short ?? '',
      description_long: apiVariant.description_long ?? '',
      code: apiVariant.code ?? '',
      sku: apiVariant.sku ?? '',
      codebar: apiVariant.barcode?.value ?? '',
      price_sale: (apiVariant.price_sale?.amount ?? '0').toString(),
      price_purchase: (apiVariant.price_purchase?.amount ?? '0').toString(),
      tax_code_sale: apiVariant.price_sale?.tax_code ?? '',
      tax_code_purchase: apiVariant.price_purchase?.tax_code ?? '',
      tax_rate_sale: apiVariant.price_sale?.tax_rate ?? 0,
      tax_rate_purchase: apiVariant.price_purchase?.tax_rate ?? 0,
      unit: typeof apiVariant.unit === 'object' ? (apiVariant.unit?.id ?? '') : (apiVariant.unit ?? ''),
      discount: {
        type: apiVariant.discount?.type ?? 'none',
        value: (apiVariant.discount?.value ?? '').toString(),
        quantity: (apiVariant.discount?.quantity ?? '').toString()
      },
      photos: [] as {name: string, size: number, type: string, view: string, file: any}[],
      features: apiVariant.features ?? []
    }
  }

  const submitProductVariantAdd = async () => {
    setLoadIndicator('on')

    if (isValidObjectAttributes(variant.valid)) {
      if (product?.id && companyForUser) {
        // Update mode: persist immediately via API
        const variantPayload = {
          ...variant.value,
          unit: resolveUnit(variant.value.unit)
        }

        const response = await ProductDA.postVariant(companyForUser.id, product.id, variantPayload)

        if (response.status === 201) {
          const created = response.data?.variant ?? response.data
          const mappedValue = mapApiVariantToState(created)
          const newValid = variant.valid

          if (variants.value.length === 0 && variants.valid.length === 0) {
            setVariants({value: [variantDefault.value, mappedValue], valid: [variantDefault.valid, newValid]})
          } else {
            setVariants({value: [...variants.value, mappedValue], valid: [...variants.valid, newValid]})
          }

          setLoadIndicator('off')
          executeHideModalProductVariantAdd()
        } else {
          GlobalSwal.fire({
            text: response.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          })
          setLoadIndicator('off')
        }
      } else {
        // Create mode: local state only
        if (variants.value.length === 0 && variants.valid.length === 0) {
          setVariants({value: [variantDefault.value, variant.value], valid: [variantDefault.valid, variant.valid]})
        } else {
          setVariants({value: [...variants.value, variant.value], valid: [...variants.valid, variant.valid]})
        }

        setLoadIndicator('off')
        executeHideModalProductVariantAdd()
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!variant.value.description_short.length) legendIconInvalid('modal-product-variant-add-input-variant-description-short', 'modal-product-variant-add-legend-variant-description-short-required')
        if (!variant.value.unit.length) legendInvalid('modal-product-variant-add-legend-variant-unit-required-add')
        if (!variant.value.tax_code_sale.length) legendInvalid('modal-product-variant-add-legend-variant-tax-sale-required-add')
        if (!variant.value.tax_code_purchase.length) legendInvalid('modal-product-variant-add-legend-variant-tax-purchase-required-add')
        if (!variant.value.price_sale.length) legendIconInvalid('modal-product-variant-add-input-variant-price-sale', 'modal-product-variant-add-legend-variant-price-sale-required')
        if (!variant.value.price_purchase.length) legendIconInvalid('modal-product-variant-add-input-variant-price-purchase', 'modal-product-variant-add-legend-variant-price-purchase-required')
        if (variant.value.discount.type !== 'none') {
          if (!variant.value.discount.value.length) legendIconInvalid('modal-product-variant-add-input-variant-discount-value', 'modal-product-variant-add-legend-variant-discount-value-required')
          if (!variant.value.discount.quantity.length) legendIconInvalid('modal-product-variant-add-input-variant-discount-quantity', 'modal-product-variant-add-legend-variant-discount-quantity-required')
        }
        for (let i = 0; i < variant.value.features.length; i++) {
          if (!variant.value.features[i].value) legendInvalid(`modal-product-variant-add-legend-variant-feature-required${suffixLegend(i)}`)
        }
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalProductVariantAdd = () => {
    modalHide('modal-product-variant-add')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-description-short', 'modal-product-variant-add-legend-variant-description-short-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-description-short', 'modal-product-variant-add-legend-variant-description-short-required')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-description-long', 'modal-product-variant-add-legend-variant-description-long-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-code', 'modal-product-variant-add-legend-variant-code-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-sku', 'modal-product-variant-add-legend-variant-sku-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-codebar', 'modal-product-variant-add-legend-variant-codebar-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-price-sale', 'modal-product-variant-add-legend-variant-price-sale-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-price-sale', 'modal-product-variant-add-legend-variant-price-sale-required')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-price-purchase', 'modal-product-variant-add-legend-variant-price-purchase-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-price-purchase', 'modal-product-variant-add-legend-variant-price-purchase-required')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-discount-value', 'modal-product-variant-add-legend-variant-discount-value-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-discount-value', 'modal-product-variant-add-legend-variant-discount-value-required')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-discount-quantity', 'modal-product-variant-add-legend-variant-discount-quantity-valid')
    legendIconValidInvalidRestart('modal-product-variant-add-input-variant-discount-quantity', 'modal-product-variant-add-legend-variant-discount-quantity-required')
    legendValidInvalidRestart('modal-product-variant-add-legend-variant-unit-required')
    legendValidInvalidRestart('modal-product-variant-add-legend-variant-tax-sale-required')
    legendValidInvalidRestart('modal-product-variant-add-legend-variant-tax-purchase-required')
    legendValidInvalidRestart('modal-product-variant-add-legend-date-required')
    legendValidInvalidRestart('modal-product-variant-add-legend-variant-gallery-max')

    for (let i = 0; i < variant.value.features.length; i++) {
      if (!variant.value.features[i].value) legendInvalid(`modal-product-variant-add-legend-variant-feature-required${suffixLegend(i)}`)
    }

    setVariant({value: {id: '', description_short: '', description_long: '', code: '', sku: '', codebar: '', price_sale: '', price_purchase: '', tax_code_sale: '', tax_code_purchase: '', tax_rate_sale: 0, tax_rate_purchase: 0, unit: '', discount: {type: 'none', value: '', quantity: ''}, photos: [], features: []}, valid: {description_short: false, description_long: true, code: true, sku: true, codebar: true, price_sale: false, price_purchase: false, tax_sale: false, tax_purchase: false, unit: false, discount: {type: true, value: true, quantity: true}, photos: [], features: [], gallery: true}})
  }

  return (
    <>
      <div id="modal-product-variant-add" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="text-capitalize">{lang.labels.addNewProductVariant}</h2>
            </div>
            <div className="modal-body">
              { companyForUser && settingsForCompany
                ?
                <AppProductVariantForm component={"modal-product-variant-add"} variant={variant} setVariant={setVariant}></AppProductVariantForm>
                :
                <AppLoading></AppLoading>
              }
            </div>
            <div className="modal-footer flex-center">
              <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalProductVariantAdd}>{lang.labels.discard}</button>
              <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitProductVariantAdd}>
                <span className="indicator-label">{lang.labels.saveVariant}</span>
                <span className="indicator-progress">
                  {lang.labels.pleaseWait}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <AppModalSettingCreateUnit companyForUser={companyForUser} options={{type: "product", main_modal: "modal-product-variant-add", index: "add", sub_index: null}} variant={variant} setVariant={setVariant} loadSettingsForCompany={loadSettingsForCompany}></AppModalSettingCreateUnit>
    </>
  )
}

export default AppModalProductVariantAdd;
